// Every state the admin shows as a pill, and the one tone each one gets.
//
// Colour carries meaning and nothing else, and a tone means the same thing
// everywhere. So there is one table, here, and `<StatusPill>` is the only
// thing that reads it: a page names the domain and the state, never a colour.
//
// UI-8 (2026-10-06) split the old four tones into six, because "warn" used
// to mean both "running" and "needs a look":
//
//   running    — blue: going on right now (küldés alatt, melegítés, épül)
//   scheduled  — indigo: waiting for its time, or for an A/B decision
//   good       — green: finished well, ready, allowed (kész, meleg)
//   warn       — orange: needs a look, or is held (tartás, szüneteltetve, lassítva)
//   danger     — red: failed, stopped, blocked
//   muted      — gray: the ordinary resting state, or "we do not know"
//
// Rule 4's zones keep their meaning exactly: over the pause line is the
// danger tone, between the raise and the pause line the warn tone.
//
// The wire value stays visible on hover (the pill's title), because the
// people debugging a stuck campaign search the logs for `snapshotting`, not
// for its Hungarian name.

export type Tone = "running" | "scheduled" | "good" | "warn" | "danger" | "muted";

/** Every tone, in the order a legend or the kit preview lists them. */
export const TONES: readonly Tone[] = ["running", "scheduled", "good", "warn", "danger", "muted"];

interface StateLook {
  tone: Tone;
  label: string;
}

const STATUS = {
  // Campaign (1.5) — CampaignState.
  campaign: {
    draft: { tone: "muted", label: "vázlat" },
    scheduled: { tone: "scheduled", label: "ütemezve" },
    snapshotting: { tone: "running", label: "összeállítás" },
    sending: { tone: "running", label: "küldés alatt" },
    paused: { tone: "warn", label: "szüneteltetve" },
    done: { tone: "good", label: "kész" },
    failed: { tone: "danger", label: "hiba" },
    cancelled: { tone: "danger", label: "leállítva" },
  },
  // Campaign group, the wizard (4.3) — CampaignGroupState.
  campaignGroup: {
    draft: { tone: "muted", label: "vázlat" },
    review: { tone: "warn", label: "ellenőrzésre vár" },
    scheduled: { tone: "scheduled", label: "ütemezve" },
    ab_testing: { tone: "scheduled", label: "A/B teszt fut" },
    ab_deciding: { tone: "scheduled", label: "A/B döntés" },
    sending: { tone: "running", label: "küldés alatt" },
    // Not on the wire: a group stays `sending` while its countries are
    // paused, and `campaignGroupShown` reads them together (2026-10-26).
    paused: { tone: "warn", label: "szüneteltetve" },
    partly_paused: { tone: "warn", label: "részben szüneteltetve" },
    done: { tone: "good", label: "kész" },
    failed: { tone: "danger", label: "hiba" },
    cancelled: { tone: "danger", label: "lemondva" },
  },
  // Segment (3.2) — SegmentState.
  segment: {
    defined: { tone: "muted", label: "definiálva" },
    requested: { tone: "running", label: "kérve" },
    materialising: { tone: "running", label: "épül" },
    ready: { tone: "good", label: "kész" },
    stale: { tone: "warn", label: "elavult" },
    failed: { tone: "danger", label: "hiba" },
    archived: { tone: "muted", label: "archiválva" },
  },
  // Template version (4.2) — TemplateStatus.
  template: {
    draft: { tone: "muted", label: "vázlat" },
    published: { tone: "good", label: "publikálva" },
    archived: { tone: "muted", label: "archiválva" },
  },
  // Test send (4.2) — TestSendState.
  testSend: {
    requested: { tone: "running", label: "kérve" },
    sending: { tone: "running", label: "küldés alatt" },
    sent: { tone: "good", label: "elküldve" },
    failed: { tone: "danger", label: "hiba" },
  },
  // What the watchdog does with a pool (2.5) — PoolWatchState.
  poolWatch: {
    watching: { tone: "muted", label: "figyeljük" },
    throttled: { tone: "warn", label: "lassítva" },
    paused: { tone: "danger", label: "megállítva" },
    manual_release: { tone: "warn", label: "kézi feloldás alatt" },
    retired: { tone: "muted", label: "leszerelve" },
  },
  // Where a pool is in its warmup (2.1) — WarmupState. UI-6 shows it.
  warmup: {
    cold: { tone: "muted", label: "hideg" },
    warming: { tone: "running", label: "melegítés" },
    holding: { tone: "warn", label: "tartás" },
    paused: { tone: "danger", label: "megállítva" },
    warm: { tone: "good", label: "meleg" },
    retired: { tone: "muted", label: "leszerelve" },
  },
  // What a pool's day was given (2.1) — `warmup_day.decision`. A verdict, like
  // `explainVerdict`: the raise is the allowed step, the hold needs a look, the
  // pause is the stop.
  warmupDecision: {
    start: { tone: "good", label: "indítás" },
    raise: { tone: "good", label: "emelés" },
    hold: { tone: "warn", label: "tartás" },
    step_back: { tone: "warn", label: "visszalépés" },
    pause: { tone: "danger", label: "megállítás" },
    manual: { tone: "warn", label: "kézi limit" },
  },
  // An outside signal (2.5) — SignalLevel. `unknown` is an answer, not a
  // gap, and it is not "bad": grey, like the ordinary state.
  signal: {
    ok: { tone: "muted", label: "rendben" },
    warn: { tone: "warn", label: "figyelmeztetés" },
    bad: { tone: "danger", label: "baj" },
    unknown: { tone: "muted", label: "nem tudjuk" },
  },
  // An operator alert (2.3) — AlertLevel.
  alert: {
    info: { tone: "muted", label: "info" },
    warning: { tone: "warn", label: "figyelmeztetés" },
    critical: { tone: "danger", label: "kritikus" },
  },
  // An exclusion row (1.1): live, or released by a person. A hold (rule 1,
  // 2026-10-16) is live until its end, then over by itself.
  suppression: {
    active: { tone: "danger", label: "aktív" },
    released: { tone: "muted", label: "feloldva" },
    held: { tone: "warn", label: "tartás" },
    ended: { tone: "muted", label: "lejárt tartás" },
  },
  // May this address be mailed (0.3, rule 6).
  sendable: {
    yes: { tone: "good", label: "küldhető" },
    no: { tone: "danger", label: "nem küldhető" },
  },
  // Whether a contact row's profile is still in its country's Klaviyo
  // account (UI-10). Present is the ordinary state, grey; absent holds the
  // row back from every send — a look, not a failure, so orange.
  presence: {
    present: { tone: "muted", label: "jelen van" },
    absent: { tone: "warn", label: "nincs jelen a Klaviyo-fiókban" },
  },
  // Whether a country's sending account is fit to send from (0.1): the
  // verdict the campaign runner refuses a start on. Not ready is a stop, so
  // it is the stop colour.
  accountReadiness: {
    ready: { tone: "good", label: "kész" },
    incomplete: { tone: "danger", label: "hiányos" },
  },
  // A country's localised wording, or one A/B arm of it (4.3): the country
  // card and the matrix both, so a state has one name on the screen. `stale`
  // is an approval of a source that has changed since — a letter switched,
  // the source text or arm edited; it holds the schedule like a missing one.
  wording: {
    missing: { tone: "danger", label: "hiányzik" },
    machine: { tone: "warn", label: "gépi fordítás" },
    human: { tone: "warn", label: "kézzel javítva" },
    approved: { tone: "good", label: "jóváhagyva" },
    stale: { tone: "danger", label: "elavult" },
  },
  // A webshop event on its way to the flow engine (7.1).
  stream: {
    pending: { tone: "running", label: "vár" },
    streamed: { tone: "muted", label: "kiment" },
  },
  // Where a bounce or complaint rate stands against rule 4 (UI-7): the zones
  // of components/emc/thresholds.ts, in the warmup page's words. `between`
  // is orange because the warmup holds; `over` is red because it stops.
  ruleZone: {
    none: { tone: "muted", label: "nincs küldés" },
    clear: { tone: "muted", label: "a határ alatt" },
    between: { tone: "warn", label: "emelés tartva" },
    over: { tone: "danger", label: "megállítási küszöb fölött" },
  },
  // One address against the blocklists (2.4, run by the 2.5 watchdog): the
  // dnsbl signal's level, in the words of a listing. `unknown` is a zone that
  // could not be asked — grey, not a verdict about the address.
  listing: {
    ok: { tone: "muted", label: "nincs listán" },
    bad: { tone: "danger", label: "listán van" },
    unknown: { tone: "muted", label: "nem ellenőrizhető" },
    warn: { tone: "warn", label: "figyelmeztetés" },
  },
  // When a watchdog source last ran (2.5 `watchdog_run`). `never` is the
  // screen's word for a source with no row: it is not configured, or the
  // loop has never reached it — "nobody asked", which is not "all fine".
  sourceRun: {
    ok: { tone: "muted", label: "lefutott" },
    failed: { tone: "danger", label: "hiba" },
    skipped: { tone: "muted", label: "kihagyva" },
    // Its last run is older than the loop should have allowed: the watchdog
    // itself may have stopped, which is the silence rule 4 cannot hear.
    late: { tone: "warn", label: "késik" },
    never: { tone: "muted", label: "nincs bekötve" },
  },
  // Google Postmaster's word for a domain or an IP (2.3), the way the 2.5
  // source reads it: HIGH and MEDIUM are fine, LOW throttles, BAD pauses.
  postmasterReputation: {
    HIGH: { tone: "muted", label: "magas" },
    MEDIUM: { tone: "muted", label: "közepes" },
    LOW: { tone: "warn", label: "alacsony" },
    BAD: { tone: "danger", label: "rossz" },
  },
  // Microsoft SNDS's filter verdict for an IP (2.3): GREEN is fine, YELLOW
  // throttles, RED pauses.
  sndsFilter: {
    GREEN: { tone: "muted", label: "zöld" },
    YELLOW: { tone: "warn", label: "sárga" },
    RED: { tone: "danger", label: "piros" },
  },
  // One address in one campaign (1.1) — RecipientState. `unknown` is the
  // honest "we cannot say whether it went", and it needs a look.
  recipient: {
    pending: { tone: "muted", label: "vár" },
    sending: { tone: "running", label: "küldés alatt" },
    sent: { tone: "good", label: "elküldve" },
    skipped_suppressed: { tone: "muted", label: "kihagyva: kizárás" },
    skipped_hygiene: { tone: "muted", label: "kihagyva: listahigiénia" },
    skipped_handover: { tone: "muted", label: "kihagyva: a Klaviyóé (migráció)" },
    failed: { tone: "danger", label: "hiba" },
    cancelled: { tone: "danger", label: "leállítva" },
    unknown: { tone: "warn", label: "bizonytalan" },
  },
  // What the provider said became of one message (1.3) — DeliveryState.
  delivery: {
    unknown: { tone: "muted", label: "nincs visszajelzés" },
    delayed: { tone: "warn", label: "késik" },
    delivered: { tone: "good", label: "kézbesítve" },
    bounced: { tone: "danger", label: "visszapattant" },
    complained: { tone: "danger", label: "panasz" },
    rejected: { tone: "danger", label: "elutasítva" },
  },
  // Who opened one message (5.5): a person (a Gmail proxy fetch is one),
  // only a machine (Apple Mail's prefetch, a scanner), or nobody.
  messageOpened: {
    person: { tone: "good", label: "megnyitotta" },
    machine: { tone: "muted", label: "csak gép" },
    none: { tone: "muted", label: "nem nyitotta meg" },
  },
  // Why an address did or did not get a campaign (3.3, rule 6) — Verdict.
  explainVerdict: {
    recipient: { tone: "good", label: "címzett" },
    eligible: { tone: "muted", label: "küldhető, még nincs összeállítva" },
    suppressed: { tone: "danger", label: "kizárva" },
    absent: { tone: "warn", label: "nincs jelen a Klaviyo-fiókban" },
    not_subscribed: { tone: "danger", label: "nincs feliratkozva" },
    frequency_capped: { tone: "warn", label: "gyakorisági korlát" },
    excluded_by_segment: { tone: "muted", label: "kizáró szegmensben" },
    excluded_by_hygiene: { tone: "warn", label: "listahigiénia kizárta" },
    not_in_audience: { tone: "muted", label: "nincs a közönségben" },
    not_in_country: { tone: "muted", label: "nem ennek az országnak a kontaktja" },
    handover: { tone: "warn", label: "a migráció alatt a Klaviyóé" },
    group_duplicate: { tone: "muted", label: "másik országból kapja" },
  },
  // A country's Klaviyo handover (0028): migrating with N, parallel (0045:
  // Klaviyo mails everybody too), or cut over.
  handover: {
    migrating: { tone: "running", label: "átadás alatt" },
    parallel: { tone: "warn", label: "párhuzamos: a Klaviyo is küld" },
    cut_over: { tone: "muted", label: "átállva" },
  },
  // A wave assignment asked for on the Warmup page (0043). Paused is a slice
  // spent or a worker restarted: it carries on by itself, so it runs.
  waveJob: {
    requested: { tone: "scheduled", label: "sorban áll" },
    running: { tone: "running", label: "fut" },
    paused: { tone: "running", label: "folytatódik" },
    done: { tone: "good", label: "kész" },
    failed: { tone: "danger", label: "hiba" },
    cancelled: { tone: "muted", label: "leállítva" },
  },
  // A Shopify shop's token (7.1): a live one, a refresh that is failing while
  // the old one still lives, one that ran out, none yet.
  shopifyToken: {
    valid: { tone: "good", label: "érvényes" },
    failing: { tone: "warn", label: "frissítés sikertelen" },
    expired: { tone: "danger", label: "lejárt" },
    none: { tone: "muted", label: "nincs még" },
  },
  // A Shopify backfill run (7.1) — ShopifyBackfillState.
  shopifyBackfill: {
    running: { tone: "running", label: "fut" },
    paused: { tone: "scheduled", label: "folytatódik" },
    done: { tone: "good", label: "kész" },
    failed: { tone: "danger", label: "hiba" },
  },
  // An admin user (4.5) — UserState. Invited waits for the person; an
  // invitation that ran out needs a new link; a switched-off user is stopped.
  user: {
    invited: { tone: "scheduled", label: "meghívva" },
    active: { tone: "good", label: "aktív" },
    expired: { tone: "warn", label: "lejárt meghívó" },
    deactivated: { tone: "danger", label: "letiltva" },
  },
  // A pulled Klaviyo stream for the flows in shadow (7.2 M2) —
  // KlaviyoSignalSourceState. Failed waits for a person (a refused key, a
  // list that is gone); a rate limit is not a state.
  signalSource: {
    active: { tone: "good", label: "húzzuk" },
    paused: { tone: "warn", label: "szünetel" },
    failed: { tone: "danger", label: "hiba" },
    retired: { tone: "muted", label: "kivezetve" },
  },
  // A Klaviyo flow's own status, as the hourly inventory last saw it (7.2):
  // live runs in Klaviyo; manual holds its letters for a person; gone is a
  // flow the account no longer lists.
  flowSource: {
    live: { tone: "good", label: "élő" },
    manual: { tone: "warn", label: "kézi" },
    draft: { tone: "muted", label: "vázlat" },
    archived: { tone: "muted", label: "archivált" },
    gone: { tone: "danger", label: "eltűnt" },
  },
  // What our engine does with a flow. No `live`: nothing here sends (7.2).
  flowMode: {
    off: { tone: "muted", label: "ki" },
    shadow: { tone: "running", label: "árnyék" },
  },
  // A flow version (0046) — FlowVersionState.
  flowVersion: {
    draft: { tone: "muted", label: "vázlat" },
    published: { tone: "good", label: "érvényes" },
    retired: { tone: "muted", label: "visszavonva" },
  },
  // An A/B test's experiment inside a flow: live splits the audience by
  // weight; completed and draft send the main letter to everybody.
  flowExperiment: {
    live: { tone: "running", label: "kísérlet fut" },
    completed: { tone: "good", label: "lezárva" },
    draft: { tone: "muted", label: "nem indult" },
  },
  // A flow letter's Klaviyo template, as the hourly import left it (7.2 M5).
  flowTemplate: {
    imported: { tone: "good", label: "importálva" },
    failed: { tone: "danger", label: "import sikertelen" },
    missing: { tone: "danger", label: "törölve a Klaviyóban" },
  },
  // Whether our copy of it could go out from us as it is.
  templateConversion: {
    ready: { tone: "good", label: "kész" },
    needs_conversion: { tone: "warn", label: "átírandó" },
  },
} as const satisfies Record<string, Record<string, StateLook>>;

export type StatusDomain = keyof typeof STATUS;

/** The look of one state. An unknown state is shown, grey, under its wire
 * name — a new enum value on the API must not crash a page or hide a row. */
export function statusLook(domain: StatusDomain, state: string): StateLook {
  const table: Record<string, StateLook> = STATUS[domain];
  return table[state] ?? { tone: "muted", label: state };
}

/** Every domain and state, for the structure test and the kit preview. */
export const STATUS_TABLE: Readonly<Record<StatusDomain, Readonly<Record<string, StateLook>>>> =
  STATUS;

// --- A campaign group, read with its countries ---------------------------------
//
// The pause is a country's (`/campaigns/{id}/pause`); the group has no paused
// state and stays `sending`, so its own state alone painted a campaign that
// nobody was sending as "küldés alatt" (owner, 2026-10-26). Every screen that
// shows a group's pill reads it through here.

/** A group's moves are over: what a country row still says does not matter. */
const GROUP_SETTLED: ReadonlySet<string> = new Set(["draft", "review", "done", "failed", "cancelled"]);
/** A country campaign that can still send. */
const COUNTRY_SETTLED: ReadonlySet<string> = new Set(["done", "failed", "cancelled"]);

/** The state a group's pill shows: `paused` when every country still live is
 * paused, `partly_paused` when some are, otherwise the group's own. */
export function campaignGroupShown(group: { state: string; countries: { state: string }[] }): string {
  if (GROUP_SETTLED.has(group.state)) return group.state;
  const live = group.countries.filter((country) => !COUNTRY_SETTLED.has(country.state));
  const paused = live.filter((country) => country.state === "paused").length;
  if (paused === 0) return group.state;
  return paused === live.length ? "paused" : "partly_paused";
}

/** The sidebar's line about what goes out: running groups over every group
 * (`counts`), less the ones whose countries are all paused (`items`, the
 * running groups the overview returned). */
export function activityLine(
  counts: Partial<Record<string, number>>,
  items: { state: string; countries: { state: string }[] }[],
): string {
  const running = ["sending", "ab_testing", "ab_deciding"].reduce((sum, state) => sum + (counts[state] ?? 0), 0);
  const paused = items.filter((group) => campaignGroupShown(group) === "paused").length;
  const going = Math.max(0, running - paused);
  const parts = [going > 0 ? `fut: ${going} kampány` : "most nem fut kampány"];
  if (paused > 0) parts.push(`szüneteltetve: ${paused}`);
  const scheduled = counts.scheduled ?? 0;
  if (scheduled > 0) parts.push(`ütemezve: ${scheduled}`);
  return parts.join(" · ");
}

// --- Words for the values that are not states ---------------------------------
//
// An exclusion's reason, where a signal came from, a campaign's mode: values a
// screen names but never colours — DESIGN.md "Komponensek": the pill is for a
// state and only a state. They live here, beside the states, so there is still
// one place that turns a wire value into Hungarian; the pages read them through
// `labelOf` or `<EnumText>` and never spell an enum themselves.

const LABELS = {
  // Why an address is excluded (1.1) — SuppressionReason.
  suppressionReason: {
    unsubscribed: "leiratkozás",
    hard_bounce: "végleges visszapattanás",
    complaint: "spam-panasz",
    blocked: "szolgáltatói tiltás",
    policy_reject: "tartalmi elutasítás",
    repeated_deferral: "ismételt késleltetés",
    spam_trap: "spam-csapda",
    role_address: "szerepcím",
    disposable: "eldobható cím",
    invalid: "érvénytelen cím",
    manual: "kézi döntés",
    imported: "Klaviyóból hozott",
    held: "tartás",
  },
  // Why a hold holds (rule 1, 2026-10-16) — HoldCause: a signal about our
  // IP or the provider, never about the address.
  holdCause: {
    block: "szolgáltatói tiltás",
    reputation: "hírnév, korlátozás",
    policy: "szabályzati elutasítás",
    mailbox_full: "megtelt postafiók",
    transient: "átmeneti hiba",
  },
  // Where the signal came from (1.1) — SuppressionSource.
  suppressionSource: {
    import: "import",
    feedback: "kézbesítési visszajelzés",
    unsubscribe: "leiratkozó link",
    hygiene: "listahigiénia",
    api: "API",
    manual: "kézi",
    // Slice 6.2: an exclusion Klaviyo holds, read through its API.
    klaviyo: "Klaviyo",
  },
  // Which rows an exclusion covers (1.1): the address everywhere, or only
  // in one country — a manual decision for one market.
  suppressionScope: {
    global: "globális",
    country: "országos",
  },
  // What the person agreed to (0.3) — ConsentStatus. Consent is what they
  // said; an exclusion is what the system learned since — two locks, and
  // neither is shown as the other.
  consent: {
    subscribed: "feliratkozott",
    unsubscribed: "leiratkozott",
    never_subscribed: "nem iratkozott fel",
    pending: "megerősítésre vár",
  },
  // A line of an exclusion's history (1.1) — SuppressionAction.
  suppressionAction: {
    suppressed: "kizárva",
    reinforced: "újabb jel",
    released: "feloldva",
    release_denied: "feloldás elutasítva",
  },
  // A campaign group's mode (4.3) — CampaignGroupMode.
  campaignMode: {
    single_language: "egynyelvű",
    localized: "lokalizált",
  },
  // A segment's kind (3.2) — SegmentKind. Dynamic: counted again at every
  // send; static: a frozen member set, rebuilt on request.
  segmentKind: {
    dynamic: "dinamikus",
    static: "statikus",
  },
  // What an A/B test counts (4.3).
  abMetric: {
    open: "nyitás",
    click: "kattintás",
  },
  // The six mailbox-provider groups (`emc_db.isp.ISPS`), in that order: the
  // deliverability tables, the anomaly panel and the reports all name them.
  isp: {
    gmail: "Gmail",
    microsoft: "Microsoft",
    yahoo: "Yahoo",
    apple: "Apple",
    local: "helyi levelezők",
    other: "egyéb",
  },
  // Who said it (2.5 watchdog sources, 2.6 anomaly, F5 injection).
  watchSource: {
    postmaster: "Google Postmaster",
    snds: "Microsoft SNDS",
    ses: "SES-fiók",
    dnsbl: "feketelisták",
    dmarc: "DMARC-riportok",
    anomaly: "saját adat (anomália)",
    campaign_check: "kampány-rajt",
    injected: "injektált próba",
  },
  // What they said it about: the watchdog's metrics (2.5) and the 2.6
  // deviation rules, which are the anomaly source's metrics.
  watchMetric: {
    domain_reputation: "domain-reputáció",
    ip_reputation: "IP-reputáció",
    spam_rate: "spam-arány",
    spf_success: "SPF-siker",
    dkim_success: "DKIM-siker",
    dmarc_success: "DMARC-siker",
    filter_result: "szűrési eredmény",
    complaint_rate: "panaszarány",
    trap_hits: "csapda-találat",
    bounce_rate: "bounce-arány",
    enforcement_status: "SES-fiók állapota",
    listing: "listázás",
    dmarc_pass_ratio: "DMARC-megfelelés",
    foreign_sender: "idegen küldő",
    human_open_drop: "emberi nyitás esése",
    deferral_spike: "halasztás-ugrás",
    deferral_surge: "halasztás-hullám",
    slow_delivery: "lassú kézbesítés",
    no_delivery: "nincs kézbesítés",
    fetch: "lekérdezés",
  },
  // The 2.6 measures, as the deviation panel names them.
  anomalyMetric: {
    delivered_ratio: "kézbesítve",
    deferred_ratio: "halasztva",
    delivery_latency_p95: "kézbesítési idő (p95)",
    human_open_ratio: "emberi nyitás",
    click_ratio: "kattintás",
  },
  // What a webshop sent (7.1) — WEBSHOP_EVENT_TYPES. The wire value stays
  // on hover: an integrator debugging a payload searches for it.
  webshopEvent: {
    registered: "regisztráció",
    viewed_product: "termék-megtekintés",
    added_to_cart: "kosárba tétel",
    started_checkout: "pénztár indítása",
    abandoned_cart: "kosárelhagyás",
    placed_order: "rendelés",
    custom: "egyedi esemény",
  },
  // An attribution window (5.2, EMC_ATTRIBUTION_WINDOWS): the headline
  // revenue is Klaviyo's, so it can be held against the Klaviyo report.
  attributionWindow: {
    klaviyo: "Klaviyo-ablak (kattintás 5 nap, nyitás 1 nap)",
    strict: "szigorú ablak (kattintás 1 nap)",
    delivered: "kézbesítési ablak (kézbesítés 5 nap)",
  },
  // Which of a pool's three logs a line comes from (UI-6).
  poolLog: {
    warmup: "warmup",
    watch: "őrszem",
    alert: "riasztás",
  },
  // What a pulled Klaviyo stream is (7.2 M2) — KlaviyoSignalKind.
  signalKind: {
    metric: "metrika",
    list: "lista",
    segment: "szegmens",
  },
} as const satisfies Record<string, Record<string, string>>;

export type LabelDomain = keyof typeof LABELS;

export interface LabelLook {
  label: string;
  /** False for a value this table does not know: shown as it came, grey. */
  known: boolean;
}

/** The Hungarian word for a value. An unknown value stays visible under its
 * wire name — a new enum value on the API must not vanish from a table. */
export function labelLook(domain: LabelDomain, value: string): LabelLook {
  const table: Record<string, string> = LABELS[domain];
  const label = table[value];
  return label === undefined ? { label: value, known: false } : { label, known: true };
}

export function labelOf(domain: LabelDomain, value: string): string {
  return labelLook(domain, value).label;
}

/** Every value a domain knows, in the table's order — for a filter's options. */
export function labelOptions(domain: LabelDomain): { value: string; label: string }[] {
  return Object.entries(LABELS[domain] as Record<string, string>).map(([value, label]) => ({
    value,
    label,
  }));
}
